import {
  CanAccess,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import {
  AlertCircle,
  CheckCircle2,
  Clipboard,
  ExternalLink,
  Facebook,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { CrmDataProvider } from "../providers/types";
import type { MetaConnection } from "../types";
import {
  getMetaConnectionStatusLabel,
  getMetaWebhookUrl,
  type MetaConnectResponse,
} from "./metaIntegrationUtils";

/**
 * UI-only provider contract for the Meta OAuth start action.
 * The implementation belongs to the active data provider and must keep all
 * Meta credentials server-side.
 */
export type MetaIntegrationsDataProvider = CrmDataProvider & {
  startMetaConnect?: () => Promise<MetaConnectResponse>;
};

export const MetaIntegrationsPanel = () => (
  <CanAccess resource="configuration" action="edit">
    <MetaIntegrationsPanelContent />
  </CanAccess>
);

const MetaIntegrationsPanelContent = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<MetaIntegrationsDataProvider>();
  const {
    data: connections = [],
    error,
    isPending,
    refetch,
  } = useGetList<MetaConnection>("meta_connections", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });
  const [isConnecting, setIsConnecting] = useState(false);

  const webhookUrl = useMemo(
    () => getMetaWebhookUrl(import.meta.env.VITE_SUPABASE_URL),
    [],
  );
  const hasStartMethod = typeof dataProvider.startMetaConnect === "function";
  const isReady = hasStartMethod && Boolean(webhookUrl);

  const handleConnect = async () => {
    if (!dataProvider.startMetaConnect) {
      notify(
        translate("crm.settings.integrations.meta.setup_required", {
          _: "Meta app credentials still need to be configured on the server before connecting.",
        }),
        { type: "warning" },
      );
      return;
    }

    setIsConnecting(true);
    try {
      const response =
        (await dataProvider.startMetaConnect()) as MetaConnectResponse;
      const url = response?.url;
      if (url) {
        window.location.assign(url);
        return;
      }

      notify(
        response?.message ??
          translate("crm.settings.integrations.meta.setup_required", {
            _: "Meta app credentials still need to be configured on the server before connecting.",
          }),
        { type: response?.error ? "error" : "warning" },
      );
    } catch (connectError) {
      notify(
        connectError instanceof Error
          ? connectError.message
          : translate("crm.settings.integrations.meta.setup_required", {
              _: "Meta app credentials still need to be configured on the server before connecting.",
            }),
        { type: "error" },
      );
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="meta-integrations-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-md border bg-muted p-2">
            <Facebook className="size-5" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">
              {translate("crm.settings.integrations.meta.title", {
                _: "Meta Lead Ads",
              })}
            </h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              {translate("crm.settings.integrations.meta.description", {
                _: "Connect Meta Instant Forms so new submissions arrive in Leads automatically.",
              })}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleConnect}
          disabled={!hasStartMethod || isConnecting}
        >
          {isConnecting ? (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : connections.length > 0 ? (
            <RefreshCw className="size-4" aria-hidden="true" />
          ) : (
            <ExternalLink className="size-4" aria-hidden="true" />
          )}
          {translate(
            connections.length > 0
              ? "crm.settings.integrations.meta.reconnect"
              : "crm.settings.integrations.meta.connect",
            { _: connections.length > 0 ? "Reconnect Meta" : "Connect Meta" },
          )}
        </Button>
      </div>

      <div
        className={cn(
          "flex items-start gap-2 rounded-md border p-3 text-sm",
          isReady
            ? "border-emerald-500/30 bg-emerald-500/5"
            : "border-amber-500/30 bg-amber-500/5",
        )}
        role="status"
      >
        {isReady ? (
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-emerald-600" />
        ) : (
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-amber-600" />
        )}
        <div>
          <p className="font-medium">
            {isReady
              ? translate("crm.settings.integrations.meta.not_connected", {
                  _: "Ready to connect",
                })
              : translate("crm.settings.integrations.meta.setup_required", {
                  _: "Meta app credentials still need to be configured on the server before connecting.",
                })}
          </p>
          {!hasStartMethod ? (
            <p className="mt-1 text-muted-foreground">
              The active data provider does not expose the Meta OAuth start
              action yet.
            </p>
          ) : null}
        </div>
      </div>

      {webhookUrl ? (
        <div className="space-y-2">
          <div>
            <p className="text-sm font-medium">
              {translate("crm.settings.integrations.meta.webhook_url", {
                _: "Webhook URL",
              })}
            </p>
            <p className="text-xs text-muted-foreground">
              {translate("crm.settings.integrations.meta.webhook_hint", {
                _: "Use this URL in the Meta Developer webhook configuration when completing the connection.",
              })}
            </p>
          </div>
          <CopyableValue value={webhookUrl} />
        </div>
      ) : null}

      {isPending ? (
        <div className="rounded-md border p-4 text-sm text-muted-foreground">
          Loading Meta connections…
        </div>
      ) : error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {translate("crm.settings.integrations.meta.error", {
            _: "Meta connection could not be loaded.",
          })}
        </div>
      ) : connections.length === 0 ? (
        <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          {translate("crm.settings.integrations.meta.no_connections", {
            _: "No Meta connections yet.",
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {connections.map((connection) => (
            <MetaConnectionCard
              key={String(connection.id)}
              connection={connection}
            />
          ))}
        </div>
      )}

      {connections.length > 0 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          className="px-0"
        >
          <RefreshCw className="size-4" aria-hidden="true" />
          Refresh connection status
        </Button>
      ) : null}
    </div>
  );
};

const MetaConnectionCard = ({ connection }: { connection: MetaConnection }) => {
  const translate = useTranslate();
  const forms = Array.isArray(connection.form_ids) ? connection.form_ids : [];
  const status = connection.enabled ? connection.status : "disabled";

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="font-medium">{connection.page_name}</p>
            {connection.business_name ? (
              <p className="text-xs text-muted-foreground">
                {connection.business_name}
              </p>
            ) : null}
          </div>
          <Badge
            variant={
              status === "error"
                ? "destructive"
                : status === "connected"
                  ? "default"
                  : "outline"
            }
          >
            {status === "connected" ? (
              <CheckCircle2 aria-hidden="true" />
            ) : null}
            {getMetaConnectionStatusLabel(translate, status)}
          </Badge>
        </div>

        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <p className="text-xs text-muted-foreground">
              {translate("crm.settings.integrations.meta.forms", {
                _: "Connected forms",
              })}
            </p>
            <p className="font-medium">
              {forms.length > 0 ? forms.join(", ") : "No forms selected"}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              {translate("crm.settings.integrations.meta.last_sync", {
                _: "Last lead received",
              })}
            </p>
            <p className="font-medium">
              {connection.last_sync_at
                ? new Date(connection.last_sync_at).toLocaleString()
                : "—"}
            </p>
          </div>
        </div>

        {connection.last_error ? (
          <p className="rounded-md bg-destructive/5 p-2 text-sm text-destructive">
            {connection.last_error}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
};

const CopyableValue = ({ value }: { value: string }) => {
  const translate = useTranslate();
  const notify = useNotify();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      notify(translate("crm.common.copied", { _: "Copied!" }));
    } catch {
      notify("Unable to copy the webhook URL", { type: "warning" });
    }
  };

  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
      <code className="min-w-0 flex-1 break-all text-xs">{value}</code>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0"
        onClick={handleCopy}
        aria-label={translate("crm.common.copy", { _: "Copy" })}
        title={translate("crm.common.copy", { _: "Copy" })}
      >
        <Clipboard className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
};
